import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import ProductCard from "@/components/ProductCard";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { SlidersHorizontal, X } from "lucide-react";

const CATS = ["", "mma-shorts", "muay-thai-shorts", "tops", "sets"];
const DISCS = ["mma", "training"];

export default function Catalogo() {
  const { t, tr, catLabel, discLabel } = useLang();
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [filters, setFilters] = useState({ sizes: [], colors: [] });
  const [loading, setLoading] = useState(true);
  const [mobileFilters, setMobileFilters] = useState(false);

  const category = params.get("category") || "";
  const gender = params.get("gender") || "all";
  const discipline = params.get("discipline") || "";
  const size = params.get("size") || "";
  const color = params.get("color") || "";
  const search = params.get("search") || "";

  const setParam = useCallback((key, val) => {
    const next = new URLSearchParams(params);
    if (val) next.set(key, val); else next.delete(key);
    setParams(next);
  }, [params, setParams]);

  useEffect(() => { api.get("/products/filters").then((r) => setFilters(r.data)); }, [tr]);

  useEffect(() => {
    setLoading(true);
    const p = {};
    if (category) p.category = category;
    if (gender && gender !== "all") p.gender = gender;
    if (discipline) p.discipline = discipline;
    if (size) p.size = size;
    if (color) p.color = color;
    if (search) p.search = search;
    api.get("/products", { params: p }).then((r) => { setProducts(r.data); setLoading(false); });
  }, [category, gender, discipline, size, color, search]);

  const activeCount = [category, gender !== "all" ? gender : "", discipline, size, color].filter(Boolean).length;
  const clearAll = () => setParams(search ? new URLSearchParams({ search }) : new URLSearchParams());

  const Panel = () => (
    <div className="space-y-8">
      <Group title={t("category")}>
        <div className="flex flex-wrap gap-2">
          {CATS.map((c) => (
            <button key={c || "all"} data-testid={`filter-cat-${c || "all"}`} onClick={() => setParam("category", c)}
              className={`px-3 py-1.5 text-xs tracking-wide border transition-colors ${category === c ? "border-vorna-accent bg-vorna-accent text-white" : "border-white/15 text-white/60 hover:border-white/40"}`}>
              {c ? catLabel(c) : t("all")}
            </button>
          ))}
        </div>
      </Group>
      <Group title={t("discipline")}>
        <div className="flex flex-wrap gap-2">
          {DISCS.map((d) => (
            <button key={d} data-testid={`filter-disc-${d}`} onClick={() => setParam("discipline", discipline === d ? "" : d)}
              className={`px-3 py-1.5 text-xs tracking-wide border transition-colors ${discipline === d ? "border-vorna-accent bg-vorna-accent text-white" : "border-white/15 text-white/60 hover:border-white/40"}`}>
              {discLabel(d)}
            </button>
          ))}
        </div>
      </Group>
      <Group title={t("gender")}>
        <div className="flex flex-wrap gap-2">
          {[{ k: "all", l: t("everyone") }, { k: "men", l: t("men") }, { k: "women", l: t("women") }, { k: "unisex", l: t("unisex") }].map((g) => (
            <button key={g.k} data-testid={`filter-gender-${g.k}`} onClick={() => setParam("gender", g.k === "all" ? "" : g.k)}
              className={`px-3 py-1.5 text-xs tracking-wide border transition-colors ${gender === g.k ? "border-vorna-accent bg-vorna-accent text-white" : "border-white/15 text-white/60 hover:border-white/40"}`}>
              {g.l}
            </button>
          ))}
        </div>
      </Group>
      <Group title={t("size")}>
        <div className="flex flex-wrap gap-2">
          {filters.sizes.length === 0 && <span className="text-xs text-white/30">—</span>}
          {filters.sizes.map((s) => (
            <button key={s} data-testid={`filter-size-${s}`} onClick={() => setParam("size", size === s ? "" : s)}
              className={`h-9 min-w-9 px-2 text-xs font-semibold border transition-colors ${size === s ? "border-vorna-accent bg-vorna-accent text-white" : "border-white/15 text-white/60 hover:border-white/40"}`}>{s}</button>
          ))}
        </div>
      </Group>
      {filters.colors.length > 0 && (
      <Group title={t("color")}>
        <div className="flex flex-wrap gap-3">
          {filters.colors.map((c) => (
            <button key={c.name} data-testid={`filter-color-${c.name}`} onClick={() => setParam("color", color === c.name ? "" : c.name)} title={c.name}
              className={`h-7 w-7 rounded-full border-2 transition-transform hover:scale-110 ${color === c.name ? "border-vorna-accent scale-110" : "border-white/20"}`} style={{ backgroundColor: c.hex }} />
          ))}
        </div>
      </Group>
      )}
    </div>
  );

  return (
    <div data-testid="catalog-page" className="pt-16 lg:pt-20">
      <Seo
        title={tr("Catálogo de Fightwear — MMA, Muay Thai y Rashes | VORNA", "Fightwear Catalogue — MMA, Muay Thai & Rash Guards | VORNA")}
        description={tr("Explora el catálogo VORNA: pantalonetas de MMA y Muay Thai, rash guards, camisetas Dryfit y conjuntos de entrenamiento. Consulta disponibilidad por WhatsApp.", "Browse the VORNA catalogue: MMA and Muay Thai shorts, rash guards, Dryfit shirts and training sets. Ask availability on WhatsApp.")}
        path="/catalogo"
      />
      <div className="border-b border-white/10 bg-vorna-surface">
        <div className="mx-auto max-w-[1600px] px-5 lg:px-10 py-12 lg:py-20">
          <p className="text-xs tracking-[0.35em] text-vorna-accent uppercase mb-3">{tr("Catálogo VORNA", "VORNA Catalogue")}</p>
          <h1 className="font-display uppercase tracking-tighter text-5xl sm:text-6xl lg:text-8xl leading-[0.82]">
            {search ? `"${search}"` : category ? catLabel(category) : t("nav_catalog")}
          </h1>
        </div>
      </div>

      <div className="mx-auto max-w-[1600px] px-5 lg:px-10 py-10 lg:py-16">
        <div className="grid lg:grid-cols-[260px_1fr] gap-10">
          <aside className="hidden lg:block">
            <div className="sticky top-28">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-display uppercase tracking-widest text-sm">{t("filters")}</h2>
                {activeCount > 0 && <button onClick={clearAll} data-testid="clear-filters" className="text-xs text-vorna-accent hover:underline">{t("clear")} ({activeCount})</button>}
              </div>
              <Panel />
            </div>
          </aside>

          <div>
            <div className="flex items-center justify-between mb-6">
              <button data-testid="mobile-filters-toggle" onClick={() => setMobileFilters(true)} className="lg:hidden inline-flex items-center gap-2 border border-white/15 px-4 py-2 text-xs tracking-widest">
                <SlidersHorizontal className="h-4 w-4" /> {t("filters")} {activeCount > 0 && `(${activeCount})`}
              </button>
              <p className="text-xs text-white/40 tracking-widest">{products.length} {t("products").toUpperCase()}</p>
            </div>

            {loading ? (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-10">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="aspect-[4/5] bg-vorna-elevated animate-pulse" />)}</div>
            ) : products.length === 0 ? (
              <div className="py-32 text-center text-white/40 font-display uppercase tracking-widest">{t("no_products")}</div>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-10">
                {products.map((p, i) => <Reveal key={p.id} delay={(i % 3) * 0.05}><ProductCard product={p} /></Reveal>)}
              </div>
            )}
          </div>
        </div>
      </div>

      {mobileFilters && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/70" onClick={() => setMobileFilters(false)} />
          <div className="absolute bottom-0 left-0 right-0 max-h-[85vh] overflow-y-auto bg-vorna-surface border-t border-white/10 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display uppercase tracking-widest">{t("filters")}</h2>
              <button onClick={() => setMobileFilters(false)}><X className="h-6 w-6" /></button>
            </div>
            <Panel />
            <button onClick={() => setMobileFilters(false)} data-testid="apply-filters" className="mt-8 w-full bg-vorna-accent py-3 font-display uppercase tracking-widest">{products.length} {t("products")}</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Group({ title, children }) {
  return (
    <div>
      <h3 className="text-[11px] font-bold tracking-widest text-white/40 uppercase mb-3">{title}</h3>
      {children}
    </div>
  );
}
